import React, { useState } from 'react'
import { Input, InputNumber, Select, Button, message } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { refineInfographic } from '../../../../api/assessmentReview'
import RefinementReview from '../shared/RefinementReview'
import ListEditor from '../shared/ListEditor'
import styles from '../shared/EditorCommon.module.css'

const PROTOCOL_INFOGRAPHIC_ORDER = ['summary', 'key_metrics', 'quick_wins', 'recommendations']

const { TextArea } = Input

const priorityOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
]

const ProtocolInfographicEditor = ({ data, pillar, onChange, onSave, onCancel, saving, clientId, assessmentId }) => {
  const [formData, setFormData] = useState(data || {})
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [showRefinedReview, setShowRefinedReview] = useState(false)
  const [refinedData, setRefinedData] = useState(null)
  const [originalData, setOriginalData] = useState(null)

  const updateFormData = (newData) => {
    setFormData(newData)
    if (onChange) {
      onChange(newData)
    }
  }

  const handleSummaryChange = (field, value) => {
    const newData = {
      ...formData,
      summary: { ...formData.summary, [field]: value }
    }
    updateFormData(newData)
  }

  const handleKeyMetricsChange = (field, value) => {
    const newData = {
      ...formData,
      key_metrics: { ...formData.key_metrics, [field]: value }
    }
    updateFormData(newData)
  }

  const handleQuickWinsChange = (index, value) => {
    const wins = [...(formData.quick_wins || [])]
    wins[index] = value
    updateFormData({ ...formData, quick_wins: wins })
  }

  const addQuickWin = () => {
    updateFormData({
      ...formData,
      quick_wins: [...(formData.quick_wins || []), '']
    })
  }

  const removeQuickWin = (index) => {
    updateFormData({
      ...formData,
      quick_wins: formData.quick_wins.filter((_, i) => i !== index)
    })
  }

  const handleRecommendationChange = (index, field, value) => {
    const recs = [...(formData.recommendations || [])]
    recs[index] = { ...recs[index], [field]: value }
    updateFormData({ ...formData, recommendations: recs })
  }

  const addRecommendation = () => {
    const recs = [...(formData.recommendations || [])]
    recs.push({
      title: 'New Recommendation',
      priority: 'medium',
      timeframe: '1-2 weeks',
      impact_score: 50,
      why_condensed: 'Description here',
      best_action: 'Action steps',
      progress_metric: 'Track progress'
    })
    updateFormData({ ...formData, recommendations: recs })
  }

  const removeRecommendation = (index) => {
    const recs = formData.recommendations.filter((_, i) => i !== index)
    updateFormData({ ...formData, recommendations: recs })
  }

  const handleAIRefine = async () => {
    if (!prompt.trim()) {
      message.error('Please enter a refinement prompt')
      return
    }
    
    if (!clientId || !assessmentId) {
      message.error('Missing client or assessment ID for AI refinement')
      return
    }

    // Sync latest formData before AI refinement
    if (onChange) {
      onChange(formData)
    }

    setLoading(true)
    try {
      const response = await refineInfographic(
        clientId,
        assessmentId,
        pillar.key,
        formData,
        prompt
      )

      const refinedContent = response.refined_content || ''
      
      try {
        const refinedParsed = typeof refinedContent === 'string' ? JSON.parse(refinedContent) : refinedContent
        setOriginalData(formData)
        setRefinedData(refinedParsed)
        setShowRefinedReview(true)
      } catch (e) {
        message.error('AI returned invalid format. Please try again.')
      }
      
    } catch (error) {
      message.error('Failed to refine infographic. Please try again.')
      console.error('Error refining:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptRefined = () => {
    if (refinedData) {
      updateFormData(refinedData)
      message.success('Infographic refined successfully!')
    }
    setShowRefinedReview(false)
    setRefinedData(null)
    setOriginalData(null)
    setPrompt('')
  }

  const handleDeclineRefined = () => {
    setShowRefinedReview(false)
    setRefinedData(null)
    setOriginalData(null)
    setPrompt('')
    message.info('Refinement declined')
  }

  return (
    <div style={{
      background: '#f8f9fa',
      padding: '20px',
      borderRadius: '8px',
      border: '2px dashed #264382',
      position: 'relative'
    }}>
      {/* Top Right Buttons */}
      <div className={styles.editorTopButtons}>
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button type="primary" onClick={onSave} loading={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Show refinement review if active */}
      {showRefinedReview && originalData && refinedData ? (
        <RefinementReview
          originalData={originalData}
          refinedData={refinedData}
          onAccept={handleAcceptRefined}
          onDecline={handleDeclineRefined}
          title={`${pillar.label} Infographic`}
          orderedKeys={PROTOCOL_INFOGRAPHIC_ORDER}
        />
      ) : (
        <>
          <div className={styles.editorSection}>
            <h3>Summary</h3>
            <div className={styles.editorRow}>
              <label>Overall Focus:</label>
                <TextArea
                value={(formData.summary && formData.summary.overall_focus) || ''}
                onChange={e => handleSummaryChange('overall_focus', e.target.value)}
                rows={3}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Top Priorities:</label>
              <InputNumber
                value={(formData.summary && formData.summary.top_priorities)}
                onChange={val => handleSummaryChange('top_priorities', val)}
                min={0}
                style={{ width: '100%' }}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Total Recommendations:</label>
              <InputNumber
                value={(formData.summary && formData.summary.total_recommendations)}
                onChange={val => handleSummaryChange('total_recommendations', val)}
                min={0}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className={styles.editorSection}>
            <h3>Key Metrics</h3>
            <div className={styles.editorRow}>
              <label>Current Adherence (%):</label>
              <InputNumber
                value={(formData.key_metrics && formData.key_metrics.current_adherence)}
                onChange={val => handleKeyMetricsChange('current_adherence', val)}
                min={0}
                max={100}
                style={{ width: '100%' }}
              />
            </div>
            <div className={styles.editorRow}>
              <label>Top Gap:</label>
              <TextArea
                value={(formData.key_metrics && formData.key_metrics.top_gap) || ''}
                onChange={e => handleKeyMetricsChange('top_gap', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <div className={styles.editorSection}>
            <h3>Quick Wins</h3>
            <ListEditor
              items={formData.quick_wins || []}
              onChange={handleQuickWinsChange}
              onAdd={addQuickWin}
              onRemove={removeQuickWin}
              placeholder="Enter quick win"
              addButtonLabel="+ Add Quick Win"
            />
          </div>

          <div className={styles.editorSection}>
            <h3>Recommendations</h3>
            {(formData.recommendations || []).map((rec, index) => (
              <div key={index} style={{
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '6px',
                marginBottom: '16px',
                border: '1px solid #e5e7eb'
              }}>
                <div className={styles.editorRow}>
                  <label>Title:</label>
                  <Input
                    value={rec.title}
                    onChange={e => handleRecommendationChange(index, 'title', e.target.value)}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Priority:</label>
                  <Select
                    value={rec.priority}
                    options={priorityOptions}
                    onChange={val => handleRecommendationChange(index, 'priority', val)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Timeframe:</label>
                  <Input
                    value={rec.timeframe}
                    onChange={e => handleRecommendationChange(index, 'timeframe', e.target.value)}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Impact Score:</label>
                  <InputNumber
                    value={rec.impact_score}
                    onChange={val => handleRecommendationChange(index, 'impact_score', val)}
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Why This Matters:</label>
                  <TextArea
                    value={rec.why_condensed}
                    onChange={e => handleRecommendationChange(index, 'why_condensed', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Best Action:</label>
                  <TextArea
                    value={rec.best_action}
                    onChange={e => handleRecommendationChange(index, 'best_action', e.target.value)}
                    rows={2}
                  />
                </div>
                <div className={styles.editorRow}>
                  <label>Progress Metric:</label>
                  <Input
                    value={rec.progress_metric}
                    onChange={e => handleRecommendationChange(index, 'progress_metric', e.target.value)}
                  />
                </div>
                <Button
                  danger
                  onClick={() => removeRecommendation(index)}
                  style={{ marginBottom: '16px' }}
                >
                  Remove Recommendation
                </Button>
              </div>
            ))}
            <Button
              type="dashed"
              onClick={addRecommendation}
              style={{ width: '100%', marginBottom: '16px' }}
            >
              + Add Recommendation
            </Button>
          </div>

          <div className={styles.editorAIRefine}>
            <div className={styles.editorAIRefineTitle}>
              <EditOutlined style={{ marginRight: '8px' }} />
              Refine with AI
            </div>
            <TextArea
              placeholder="Enter refinement instructions (e.g., 'Make recommendations more urgent', 'Focus on intensity')"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={2}
              style={{ marginBottom: '8px' }}
            />
            <Button
              type="primary"
              onClick={handleAIRefine}
              loading={loading}
              disabled={!prompt.trim()}
              icon={<EditOutlined />}
            >
              {loading ? 'Refining...' : 'Refine with AI'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default ProtocolInfographicEditor
